import moment from "moment";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import ReactToPrint from "react-to-print";
import * as XLSX from 'xlsx';
import {Button} from "../../../components/Button";
import {FooterPaginas} from "../../../components/FooterPaginas/FooterPaginas";
import Form from "../../../components/Form";
import {Input} from "../../../components/Input";
import Paginacao from "../../../components/Paginacao";
import {Select} from "../../../components/Select";
import {SkeletonTableCestas} from "../../../components/Skeletons/SkeletonTableCestas/SkeletonTableCestas";
import Table from "../../../components/Table/Table";
import {RelatoriosContext} from "../../../contexts/Relatorios/RelatoriosContext";
import {formatValue} from "../../../services/Functions";
import {cepMask, cpfMask, phoneMask} from "../../../services/Masks";
import {Relatorio as RelatorioType} from "../../../types/Relatorios";
import PrintableRelatorio from "../PrintableRelatorio/PrintableRelatorio";
import "./styles.ts";
import RelatorioWrapper from "./styles";

interface RelatorioProps {
    relatorio: RelatorioType;
}

const Relatorio: React.FC<RelatorioProps> = ({relatorio}) => {
    const {gerarRelatorio, exportarRelatorio} = useContext(RelatoriosContext);

    const tableRef = useRef<HTMLTableElement>(null);

    //paginacao
    const [currentPage, setCurrentPage] = useState(1);
    const [itensPorPagina, setItensPorPagina] = useState(50);
    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(false);
    const [filtros, setFiltros] = useState<any>({});
    const [formValid, setFormValid] = useState(false);

    const [relatorioData, setRelatorioData] = useState<Array<{}>>();
    const [relatorioColumns, setRelatorioColumns] = useState<
        Array<{ key: string; label: string }>
    >([]);
    const [sortedData, setSortedData] = useState<Array<{}>>([]);

    const handleFiltrar = async () => {
        setLoading(true);
        const res = await gerarRelatorio(relatorio.id!, filtros);
        if (res.length === 0) return setRelatorioData([]);

        let columns: any = [];
        Object.keys(res[0]).forEach((key) => {
            columns.push({
                key: key,
                label: formatarLabel(key),
                mask: applyMask(key),
                allowHTML: false,
            });
        });

        setRelatorioColumns(columns);
        setRelatorioData(res);
        setTotalPages(Math.ceil(res.length / itensPorPagina));
        setCurrentPage(1);
    };

    const EXCEL_CELL_LIMIT = 32767;

    const serializarVariacao = (variacao: any): string => {
        if (variacao === null || variacao === undefined) return '';
        if (typeof variacao !== 'object') return String(variacao);

        const displayText = variacao.nome_variacao
            || variacao.preco
            || variacao.referencia
            || variacao.produto_referencia
            || variacao.produto_nome
            || variacao.produto_info
            || variacao.nome
            || '';
        const preco = variacao.preco !== undefined && variacao.preco !== null && variacao.nome_variacao
            ? ` - ${variacao.preco}`
            : '';
        const quantidade = variacao.quantidade !== undefined && variacao.quantidade !== null
            ? ` - ${variacao.quantidade}`
            : '';
        const texto = `${displayText}${preco}${quantidade}`.trim();
        return texto || JSON.stringify(variacao);
    };

    const serializarValorParaExcel = (value: any): any => {
        if (value === null || value === undefined) return value;

        let resultado: any = value;

        if (Array.isArray(value)) {
            resultado = value.map(serializarVariacao).join(' | ');
        } else if (typeof value === 'object') {
            resultado = serializarVariacao(value);
        } else if (typeof value === 'string') {
            resultado = value.replace(/^="(.+)"$/, '$1');
            resultado = resultado.replace(/^=/, '');
            resultado = resultado.replace(/^"/, '').replace(/"$/, '');
        }

        if (typeof resultado === 'string' && resultado.length > EXCEL_CELL_LIMIT) {
            const sufixo = '...[conteúdo truncado]';
            resultado = resultado.substring(0, EXCEL_CELL_LIMIT - sufixo.length) + sufixo;
        }

        return resultado;
    };

    const limparDadosParaExportacao = (data: any[]) => {
        return data.map(item => {
            const cleanItem: any = {};
            Object.keys(item).forEach(key => {
                cleanItem[key] = serializarValorParaExcel(item[key]);
            });
            return cleanItem;
        });
    };

    const formatarLabel = (label: string) => {
        let newLabel = label.replace(/_/g, " ");
        newLabel = newLabel.charAt(0).toUpperCase() + newLabel.slice(1);
        return newLabel;
    };

    const renderVariacoes = (value: any) => {
        if (!value || typeof value === 'string') return value;

        const variations = Array.isArray(value) ? value : [value];

        return (
            <>
                {variations.map((variacao, index) => {
                    const displayText = variacao.preco || 
                                      variacao.referencia || 
                                      variacao.nome_variacao || 
                                      variacao.produto_referencia ||
                                      variacao.produto_nome ||
                                      variacao.produto_info ||
                                      variacao.nome ||
                                      'Item';
                    
                    const quantidade = variacao.quantidade || 0;

                    return (
                        <div key={index}>
                            <span>
                                {displayText}{quantidade > 0 ? ` - ${quantidade}` : ''}
                            </span>
                            {index < variations.length - 1 && <br/>}
                        </div>
                    );
                })}
            </>
        );
    };

    const applyMask = (key: string) => {
        switch (key.toLowerCase().replace(/ /g, "_").replace(/"/g, "")) {
            case "data":
                return (value: string) => {
                    if (value.includes(" ") || value.includes(":")) {
                        return moment(value).format("DD/MM/YYYY HH:mm:ss");
                    } else {
                        return moment(value).format("DD/MM/YYYY");
                    }
                };
            case "valor_total":
            case "valor_total_sem_desconto":
            case "valor_total_com_desconto":
            case "preco_unitario":
            case "valor":
            case "estimativa_de_vendas_no_catálogo":
            case "ticket_médio_dos_pedidos":
            case "ticket_médio_dos_produtos":
                return (value: string) => {
                    return formatValue(value, 2, true);
                };
            case "cpf":
                return cpfMask;
            case "cep":
                return cepMask;
            case "telefone":
                return phoneMask;
            case "variacoes":
                return (value: { preco: string; quantidade: number; nome_variacao: string }[]) => {
                    if (!value || typeof value === 'string') return value;

                    const variations = Array.isArray(value) ? value : [value];

                    return (
                        <>
                            {variations.map((variacao, index) => (
                                <div key={index}>
                                    <span>
                                        {variacao.nome_variacao} - {variacao.preco} - {variacao.quantidade}
                                    </span>
                                    {index < variations.length - 1 && <br />}
                                </div>
                            ))}
                        </>
                    );
                };
            default:
                return (value: string) => value;
        }
    };

    const memoizedTableColumn = useMemo(
        () => relatorioColumns,
        [relatorioColumns]
    );

    const exportarRelatorioDowload = useCallback(() => {
        if (!sortedData || sortedData.length === 0) {
            alert('Nenhum dado para exportar');
            return;
        }

        const dadosLimpos = limparDadosParaExportacao(sortedData);

        const worksheet = XLSX.utils.json_to_sheet(dadosLimpos);
        const workbook = XLSX.utils.book_new();
        const nomeSheet = (relatorio.nome || 'Relatório').substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, nomeSheet);

        const dataAtual = moment().format('DD-MM-YYYY_HH-mm-ss');
        const nomeArquivo = `${relatorio.nome || 'Relatorio'}_${dataAtual}.xlsx`;

        XLSX.writeFile(workbook, nomeArquivo);
    }, [sortedData, relatorio.nome]);

    useEffect(() => {
        if (relatorioData) {
            setLoading(false);
        }
    }, [relatorioData]);

    useEffect(() => {
        if (relatorio.variables) {
            let newFiltros: any = {};
            relatorio.variables?.forEach((variable) => {
                if (variable.type === "datetime-local") {
                    if (variable.label === "De") {
                        newFiltros[variable.variable!] = moment()
                            .subtract(1, "months")
                            .format("YYYY-MM-DDTHH:mm");
                    } else {
                        newFiltros[variable.variable!] =
                            moment().format("YYYY-MM-DDTHH:mm");
                    }
                } else {
                    newFiltros[variable.variable!] = "";
                }
                if (variable.label === "Catálogo" || variable.variable === "satelite") {
                    newFiltros[variable.variable!] =
                        variable.options && variable.options.length
                            ? variable.options[0].value
                            : "";
                }
                if (variable.label === "Categorias") {
                    newFiltros[variable.variable!] = variable.options
                        ? variable.options[0].value
                        : "";
                }
            });
            setFiltros(newFiltros);
        } else {
            handleFiltrar();
        }
    }, [relatorio]);

    return (
        <RelatorioWrapper>
            {relatorio.variables && relatorio.variables.length && (
                <Form
                    className="relatorio-header"
                    id="form-variables-relatorios"
                    setFormValid={setFormValid}
                    onSubmit={handleFiltrar}
                >
                    <div className="variables-wrapper">
                        {relatorio.variables.map((variable) => {
                            // SKU opcional em "Todos os produtos por categoria":
                            // basta escolher a categoria pra listar todos os produtos dela
                            const skuOpcional =
                                variable.variable === "referencia" &&
                                !!relatorio.nome
                                    ?.toLowerCase()
                                    .includes("produtos por categoria");

                            return (
                            <>
                                {(() => {
                                    switch (variable.tag) {
                                        case "input":
                                            return (
                                                <div className="input-wrapper">
                                                    <Input
                                                        type={variable.type!}
                                                        value={filtros[variable.variable!]}
                                                        label={variable.label!}
                                                        placeholder={variable.placeholder!}
                                                        onChange={(newValue) => {
                                                            setFiltros({
                                                                ...filtros,
                                                                [variable.variable!]: newValue,
                                                            });
                                                        }}
                                                        required={!skuOpcional}
                                                    />
                                                </div>
                                            );
                                        case "select":
                                            return (
                                                <div className="select-wrapper">
                                                    <Select
                                                        options={variable.options!}
                                                        value={filtros[variable.variable!]}
                                                        label={variable.label!}
                                                        sizeLabel="medium"
                                                        placeholder={variable.placeholder!}
                                                        onChange={(newValue) => {
                                                            setFiltros({
                                                                ...filtros,
                                                                [variable.variable!]: newValue,
                                                            });
                                                        }}
                                                        required={true}
                                                    />
                                                </div>
                                            );
                                        default:
                                            return <div></div>;
                                    }
                                })()}
                            </>
                            );
                        })}
                    </div>
                    <Button
                        className={`fonteTexto ${!formValid ? "disabled" : ""}`}
                        type="submit"
                        title="Filtrar"
                    />
                </Form>
            )}
            {relatorioData && !loading ? (
                <>
                    <Table
                        className="screen-table"
                        columns={relatorioColumns}
                        data={relatorioData!}
                        sortable={true}
                        selectable={false}
                        button={false}
                        page={currentPage}
                        mostrarTotais={true}
                        itensPorPagina={itensPorPagina}
                        onSortedDataChange={setSortedData}
                    />
                    <PrintableRelatorio
                        ref={tableRef}
                        relatorio={relatorio}
                        table={
                            <Table
                                className="printable-table"
                                columns={memoizedTableColumn}
                                data={sortedData!}
                                sortable={false}
                                selectable={false}
                                button={false}
                                mostrarTotais={true}
                                page={1}
                                itensPorPagina={-1}
                            />
                        }
                    />
                </>
            ) : (
                loading && <SkeletonTableCestas/>
            )}
            {relatorioData && relatorioData.length > 0 && !loading && (
                <FooterPaginas>
                    <div className="flex-footer-relatorios">
            <span>
              <Paginacao
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
              ></Paginacao>
            </span>
                        <span className="botoes">
              <Button
                  className="botao exportar"
                  title=""
                  onClick={exportarRelatorioDowload}
              ></Button>
              <ReactToPrint
                  removeAfterPrint={true}
                  trigger={() => <Button className="botao imprimir" title=""/>}
                  content={() => tableRef.current}
              />
            </span>
                        {relatorioData &&
                            relatorioData.length > 0 &&
                            relatorioColumns.find(
                                // || campo.key === "valor" | Caso tiver um novo campo chamado "valor"
                                (campo) => campo.key === "valor_total"
                            ) && (
                                <span className="valor">
                  <div className="valor-flex">
                    <span className="texto">Valor total:</span>
                    <span>
                      {(relatorioData &&
                              relatorioData
                                  .reduce((acumulador: number, data: any) => {
                                      return (
                                          acumulador +
                                          Number.parseFloat(data.valor_total) || 0
                                      );
                                  }, 0)
                                  .toLocaleString("pt-br", {
                                      style: "currency",
                                      currency: "BRL",
                                  })) ||
                          ""}
                    </span>
                  </div>
                </span>
                            )}
                    </div>
                </FooterPaginas>
            )}
        </RelatorioWrapper>
    );
};

export default Relatorio;
